import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from '../../i18n';
import { moveNode, readTreeRows, padKey } from '../../extensions/bundled/documents/documentsStore';
import { useNotificationStore } from '../../store/notificationStore';
import { notifyMcpSurfaceChanged } from '../../mcp/notify';
import { KEY_STEP } from './treeModel';
import { makeDragGhost } from './dragGhost';

/** Hovering a closed folder / page this long while dragging opens it (card 1.7). */
export const HOVER_EXPAND_MS = 600;
const ROOT = '__root__';

// The drag in flight, across every section: a dragover cannot read the
// dataTransfer payload, and a drop needs to know whether the row came from
// this tree at all (a drop from another scope is refused, not executed).
let currentDrag = null; // { sectionId, nodeId, node, scope }

/**
 * The tree row being dragged, or null — read by the workspace switcher (card
 * 5.3), which takes drops from the tree without being part of it.
 */
export const currentTreeDrag = () => currentDrag;

/** 'before' | 'inside' | 'after' from the pointer's height in the row (25 / 50 / 25). */
export function dropPositionAt(rect, y) {
  const ratio = (y - rect.top) / Math.max(rect.height, 1);
  if (ratio <= 0.25) return 'before';
  if (ratio >= 0.75) return 'after';
  return 'inside';
}

/**
 * Drag and drop for one section's tree (card 1.7) with native HTML5 drag
 * events — no dependency; Tauri's own file-drop handling is off
 * (`dragDropEnabled: false`), so WebView2 delivers them. Dropping ON a row nests
 * the dragged node at its end, dropping on its top / bottom quarter reorders
 * (the same resequencing `moveNode` pass as before). A closed row hovered for
 * HOVER_EXPAND_MS opens. Escape cancels (the browser ends the drag).
 *
 * Also the keyboard alternative: `moveByKey` — Alt+Up / Alt+Down reorder,
 * Alt+Right nests into the previous sibling, Alt+Left moves out one level.
 */
export default function useTreeDnd({ sectionId, scope, tree, editingId }) {
  const { nodeById, orderedChildren, isAncestor, load, expandFolder, expandedFolders, childrenByParent, searching } = tree;
  const [draggingId, setDraggingId] = useState(null);
  const [drop, setDrop] = useState(null); // { id, position } | null
  const expandRef = useRef({ id: null, timer: null });
  const disabled = editingId != null || searching;

  const clearExpand = () => {
    clearTimeout(expandRef.current.timer);
    expandRef.current = { id: null, timer: null };
  };
  useEffect(() => clearExpand, []);

  /** Moves srcNode under newParentId at `index`, resequencing that sibling group. */
  const applyMove = useCallback(
    async (srcNode, newParentId, index) => {
      // Cycle guard for every node type: a page dropped into its own
      // descendant would detach the whole subtree from the root.
      if (newParentId === srcNode.id) return;
      if (newParentId && isAncestor(srcNode.id, newParentId)) return;
      const siblings = orderedChildren(newParentId, srcNode.id);
      const clamped = Math.max(0, Math.min(index, siblings.length));
      const newOrder = [...siblings.slice(0, clamped), srcNode, ...siblings.slice(clamped)];
      const parent = newParentId ?? null;
      // ONE whole-tree read for the whole pass (moveNode carries every other
      // data key forward and would otherwise re-read the tree per sibling).
      const rows = await readTreeRows(scope);
      for (let i = 0; i < newOrder.length; i++) {
        const child = newOrder[i];
        const key = padKey((i + 1) * KEY_STEP);
        if (child.id === srcNode.id) {
          await moveNode(srcNode, parent, key, scope, rows); // eslint-disable-line no-await-in-loop
        } else if ((child.parentId ?? null) !== parent || child.sortKey !== key) {
          await moveNode(child, parent, key, scope, rows); // eslint-disable-line no-await-in-loop
        }
      }
      notifyMcpSurfaceChanged('documents');
      await load();
    },
    [orderedChildren, isAncestor, load, scope]
  );

  /** Executes a drop of `srcNode` on `targetId` (ROOT = end of the tree) at `position`. */
  const dropOn = useCallback(
    async (srcNode, targetId, position) => {
      if (targetId === ROOT) return applyMove(srcNode, null, Number.MAX_SAFE_INTEGER);
      const dstNode = nodeById.get(targetId);
      if (!dstNode || dstNode.id === srcNode.id) return undefined;
      if (position === 'inside') {
        expandFolder(dstNode.id);
        return applyMove(srcNode, dstNode.id, Number.MAX_SAFE_INTEGER);
      }
      const parentId = dstNode.parentId ?? null;
      const siblings = orderedChildren(parentId, srcNode.id);
      const at = siblings.findIndex((n) => n.id === dstNode.id);
      return applyMove(srcNode, parentId, position === 'after' ? at + 1 : at);
    },
    [nodeById, orderedChildren, applyMove, expandFolder]
  );

  const endDrag = useCallback(() => {
    currentDrag = null;
    clearExpand();
    setDraggingId(null);
    setDrop(null);
  }, []);

  /** Accept (or not) a dragover; returns the drop to show, or null to refuse. */
  const accept = (e, targetId, position) => {
    if (!currentDrag) return null;
    const foreign = currentDrag.sectionId !== sectionId;
    if (!foreign) {
      const src = currentDrag.nodeId;
      if (targetId === src || (targetId !== ROOT && isAncestor(src, targetId))) return null;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    return foreign ? { id: targetId, position: 'refused' } : { id: targetId, position };
  };

  const refuseForeign = () => {
    useNotificationStore
      .getState()
      .warning(i18n.t('docSidebar.dnd.crossScope'), i18n.t('docSidebar.dnd.crossScopeTitle'));
  };

  const finishDrop = (e, targetId, position) => {
    e.preventDefault();
    e.stopPropagation();
    const drag = currentDrag;
    endDrag();
    if (!drag) return;
    if (drag.sectionId !== sectionId) {
      refuseForeign();
      return;
    }
    const src = nodeById.get(drag.nodeId);
    if (src) dropOn(src, targetId, position);
  };

  const rowDragProps = (node) => {
    if (disabled) return {};
    return {
      draggable: true,
      onDragStart: (e) => {
        currentDrag = { sectionId, nodeId: node.id, node, scope };
        e.dataTransfer.effectAllowed = 'move';
        try {
          e.dataTransfer.setData('application/x-apiboo-document', String(node.id));
          e.dataTransfer.setData('text/plain', node.name || '');
          const ghost = makeDragGhost(node);
          if (ghost) e.dataTransfer.setDragImage(ghost, 18, 15);
        } catch {
          /* jsdom / an old WebView: the default drag image is fine */
        }
        setDraggingId(node.id);
      },
      onDragOver: (e) => {
        e.stopPropagation();
        const position = dropPositionAt(e.currentTarget.getBoundingClientRect(), e.clientY);
        const next = accept(e, node.id, position);
        setDrop((prev) => (prev?.id === next?.id && prev?.position === next?.position ? prev : next));
        const closed = (node.type === 'folder' || (childrenByParent.get(node.id) || []).length > 0) && !expandedFolders.has(node.id);
        if (next && next.position === 'inside' && closed) {
          if (expandRef.current.id !== node.id) {
            clearExpand();
            expandRef.current = { id: node.id, timer: setTimeout(() => expandFolder(node.id), HOVER_EXPAND_MS) };
          }
        } else if (expandRef.current.id === node.id) clearExpand();
      },
      onDragLeave: (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        if (expandRef.current.id === node.id) clearExpand();
        setDrop((prev) => (prev?.id === node.id ? null : prev));
      },
      onDrop: (e) => finishDrop(e, node.id, dropPositionAt(e.currentTarget.getBoundingClientRect(), e.clientY)),
      onDragEnd: endDrag,
    };
  };

  /** The end-of-tree / empty-tree drop zone: drops there land last at the top level. */
  const rootDropProps = {
    onDragOver: (e) => {
      const next = accept(e, ROOT, 'end');
      setDrop((prev) => (prev?.id === next?.id ? prev : next));
    },
    onDragLeave: () => setDrop((prev) => (prev?.id === ROOT ? null : prev)),
    onDrop: (e) => finishDrop(e, ROOT, 'end'),
  };

  /** Alt+arrow moves. Resolves true when the key was one of them (the caller then prevents default). */
  const moveByKey = (node, key) => {
    if (disabled) return false;
    const parentId = node.parentId ?? null;
    const siblings = orderedChildren(parentId);
    const at = siblings.findIndex((n) => n.id === node.id);
    if (key === 'ArrowUp') {
      if (at > 0) applyMove(node, parentId, at - 1);
      return true;
    }
    if (key === 'ArrowDown') {
      if (at >= 0 && at < siblings.length - 1) applyMove(node, parentId, at + 1);
      return true;
    }
    if (key === 'ArrowRight') {
      const prev = at > 0 ? siblings[at - 1] : null;
      if (prev) {
        expandFolder(prev.id);
        applyMove(node, prev.id, Number.MAX_SAFE_INTEGER);
      }
      return true;
    }
    if (key === 'ArrowLeft') {
      const parent = parentId == null ? null : nodeById.get(parentId);
      if (parent) {
        const grand = parent.parentId ?? null;
        const at2 = orderedChildren(grand).findIndex((n) => n.id === parent.id);
        applyMove(node, grand, at2 + 1);
      }
      return true;
    }
    return false;
  };

  return { draggingId, drop, rowDragProps, rootDropProps, rootDropActive: drop?.id === ROOT, moveByKey, applyMove };
}
